import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

export type CustomerRefusalOperationStatus =
  | 'notStarted'
  | 'pending'
  | 'retryScheduled'
  | 'completed';

export type CustomerRefusalOutcomeView =
  | 'customer'
  | 'seller'
  | 'accounting'
  | 'admin';

export interface CustomerRefusalFinancialOutcome {
  finalizationId: string;
  sellerOrderId: string;
  routeId: string;
  stopId: string;
  recoveryId: string;
  driverId: string;
  reasonCode: string;
  reasonText: string;
  refusedAtUtc: string;
  returnedAtUtc: string;
  paymentMethod: string;
  paymentAttemptId: string | null;
  outcome: 'codNoRefund' | 'walletRefund';
  customerRefundAmount: number;
  originalShippingRefundAmount: number;
  sellerLiabilityAmount: number;
  currency: string;
  walletStatus: CustomerRefusalOperationStatus;
  walletCreditId: string | null;
  walletCompletedAtUtc: string | null;
  sellerLiabilityStatus: CustomerRefusalOperationStatus;
  sellerLiabilityPostingId: string | null;
  sellerAdjustedAmount: number | null;
  sellerDebtAmount: number | null;
  sellerLiabilityCompletedAtUtc: string | null;
}

@Component({
  selector: 'sham-customer-refusal-outcome',
  imports: [DatePipe, RouterLink, TranslateModule],
  templateUrl: './customer-refusal-outcome.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerRefusalOutcome {
  readonly outcome = input.required<CustomerRefusalFinancialOutcome>();
  readonly view = input.required<CustomerRefusalOutcomeView>();
  readonly walletLink = input<string | null>(null);
  readonly refresh = output<void>();

  showWallet(): boolean {
    return this.view() === 'customer' || this.view() === 'admin';
  }

  showSellerLiability(): boolean {
    return this.view() !== 'customer';
  }

  showOperations(): boolean {
    return this.view() === 'admin';
  }

  statusLabel(status: CustomerRefusalOperationStatus): string {
    switch (status) {
      case 'notStarted':
        return 'Not started';
      case 'pending':
        return 'Pending';
      case 'retryScheduled':
        return 'Retry scheduled';
      case 'completed':
        return 'Completed';
    }
  }

  statusClass(status: CustomerRefusalOperationStatus): string {
    switch (status) {
      case 'completed':
        return 'badge badge-sm badge-soft badge-success';
      case 'retryScheduled':
        return 'badge badge-sm badge-soft badge-warning';
      case 'pending':
        return 'badge badge-sm badge-soft badge-info';
      case 'notStarted':
        return 'badge badge-sm badge-ghost';
    }
  }
}
